import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

/* ═══════════════════════════════════════════════════════
   BED SVG ICON
═══════════════════════════════════════════════════════ */
const BedSVG = ({ width = 200, height = 130, variant = "default" }) => {
  const isGold = variant === "gold";
  const isLight = variant === "light";
  const headColor    = isGold ? "#c9a84c" : isLight ? "#b08a3e" : "#7a5f20";
  const postColor    = isGold ? "#e8c96a" : isLight ? "#d4a84c" : "#c9a84c";
  const railColor    = isGold ? "#b08220" : isLight ? "#8a6820" : "#5a3f10";
  const mattressStroke = isGold ? "#c9a84c" : isLight ? "#c9a84c" : "#5a3f10";
  const legColor     = isGold ? "#a08028" : isLight ? "#8a6820" : "#4a3010";
  const duvetPath    = isGold ? "#c9a84c" : isLight ? "#b08a3e" : "#3a2a0a";

  return (
    <svg viewBox="0 0 200 130" xmlns="http://www.w3.org/2000/svg" width={width} height={height}>
      <rect x="10" y="18" width="16" height="72" rx="4" fill={headColor} />
      <rect x="13" y="8"  width="10" height="14" rx="5" fill={postColor} />
      <rect x="174" y="42" width="16" height="48" rx="4" fill={headColor} />
      <rect x="177" y="34" width="10" height="12" rx="5" fill={postColor} />
      <rect x="10" y="82" width="180" height="13" rx="3" fill={railColor} />
      <rect x="26" y="50" width="148" height="33" rx="6"
        fill={isLight ? "#f5f0e8" : "#1a1206"}
        stroke={mattressStroke} strokeWidth="1.5" />
      <rect x="32" y="54" width="40" height="22" rx="5" fill={isLight ? "#fff" : "#f5f0e8"} opacity="0.95"/>
      <line x1="52" y1="54" x2="52" y2="76" stroke="#ddd0b8" strokeWidth="0.8"/>
      <rect x="80" y="54" width="40" height="22" rx="5" fill={isLight ? "#fff" : "#f5f0e8"} opacity="0.95"/>
      <line x1="100" y1="54" x2="100" y2="76" stroke="#ddd0b8" strokeWidth="0.8"/>
      <rect x="124" y="50" width="50" height="33" rx="6"
        fill={isLight ? "#e8dcc8" : "#241a06"}
        stroke={isGold ? "rgba(201,168,76,0.5)" : "none"} strokeWidth="0.8" />
      <path d="M124 65 Q149 57 174 65" stroke={duvetPath} strokeWidth="1.5" fill="none"/>
      <rect x="12"  y="95" width="6" height="18" rx="2" fill={legColor}/>
      <rect x="182" y="95" width="6" height="18" rx="2" fill={legColor}/>
      {isGold && <ellipse cx="100" cy="113" rx="80" ry="6" fill="#c9a84c" opacity="0.15"/>}
    </svg>
  );
};

/* ═══════════════════════════════════════════════════════
   PROFESSIONAL SUCCESS LOADER OVERLAY
═══════════════════════════════════════════════════════ */
const SuccessOverlay = ({ visible, userName }) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(0); // 0=loading, 1=done
  const rafRef = useRef(null);

  useEffect(() => {
    if (!visible) { setProgress(0); setPhase(0); return; }
    let startTime = null;
    const DURATION = 2800;
    const tick = (ts) => {
      if (!startTime) startTime = ts;
      const pct = Math.min(((ts - startTime) / DURATION) * 100, 100);
      setProgress(pct);
      if (pct >= 100) { setPhase(1); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [visible]);

  if (!visible) return null;
  const firstName = userName ? userName.split(" ")[0] : "Guest";

  const steps = ["Verifying credentials", "Loading your workspace", "Setting preferences", "Almost ready"];
  const currentStep = Math.min(Math.floor((progress / 100) * steps.length), steps.length - 1);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "linear-gradient(145deg, #fdf9f0 0%, #fef8ed 30%, #fffaf2 60%, #fdf6e8 100%)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      animation: "loaderFadeIn 0.4s ease forwards",
    }}>
      {/* Decorative background pattern */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage:
          "radial-gradient(circle at 20% 80%, rgba(201,168,76,0.08) 0%, transparent 50%)," +
          "radial-gradient(circle at 80% 20%, rgba(201,168,76,0.06) 0%, transparent 50%)," +
          "radial-gradient(circle at 50% 50%, rgba(201,168,76,0.04) 0%, transparent 70%)",
      }}/>

      {/* Grid lines */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage:
          "linear-gradient(rgba(201,168,76,0.06) 1px, transparent 1px)," +
          "linear-gradient(90deg, rgba(201,168,76,0.06) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}/>

      {/* Diagonal accent */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        overflow: "hidden",
      }}>
        {[...Array(4)].map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            left: "-20%", top: `${10 + i * 25}%`,
            width: "140%", height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.12), transparent)",
            transform: `rotate(${-3 + i}deg)`,
          }}/>
        ))}
      </div>

      {/* Center content */}
      <div style={{
        textAlign: "center", position: "relative", zIndex: 1,
        animation: "loaderContentIn 0.6s cubic-bezier(.16,1,.3,1) 0.1s both",
        padding: "0 24px",
      }}>
        {/* Top ornament */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", justifyContent: "center", marginBottom: "40px" }}>
          <div style={{ width: "60px", height: "1px", background: "linear-gradient(90deg, transparent, #c9a84c)" }}/>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#c9a84c" opacity="0.8">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
          </svg>
          <div style={{ width: "60px", height: "1px", background: "linear-gradient(90deg, #c9a84c, transparent)" }}/>
        </div>

        {/* Bed illustration with glow ring */}
        <div style={{ position: "relative", display: "inline-block", marginBottom: "32px" }}>
          {/* Glow ring */}
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)",
            width: "180px", height: "180px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(201,168,76,0.15) 0%, transparent 70%)",
            animation: "glowPulse 2s ease-in-out infinite",
          }}/>
          {/* Ring border */}
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)",
            width: "150px", height: "150px",
            borderRadius: "50%",
            border: "1.5px dashed rgba(201,168,76,0.25)",
            animation: "spin 12s linear infinite",
          }}/>
          <div style={{
            position: "relative", zIndex: 1,
            animation: "bedFloat 2.6s ease-in-out infinite",
            filter: "drop-shadow(0 8px 20px rgba(201,168,76,0.3))",
          }}>
            <BedSVG width={180} height={116} variant="gold"/>
          </div>
        </div>

        {/* Welcome text */}
        <div style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: "clamp(24px, 5vw, 34px)",
          fontWeight: 700,
          color: "#2c1f0a",
          letterSpacing: "0.02em",
          marginBottom: "6px",
          lineHeight: 1.2,
        }}>
          Welcome Back, <span style={{ color: "#c9a84c" }}>{firstName}</span>
        </div>

        <div style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "11px", fontWeight: 500,
          color: "#a08030",
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          marginBottom: "8px",
        }}>
          Serena Beds Ltd
        </div>

        {/* Step indicator */}
        <div style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "13px",
          color: "#8a6820",
          marginBottom: "36px",
          height: "20px",
          opacity: phase === 1 ? 0 : 1,
          transition: "opacity 0.3s",
        }}>
          {phase === 0 ? `${steps[currentStep]}…` : ""}
        </div>

        {/* Progress track */}
        <div style={{ width: "min(320px, 80vw)", margin: "0 auto 12px" }}>
          {/* Track */}
          <div style={{
            height: "6px",
            background: "rgba(201,168,76,0.15)",
            borderRadius: "99px",
            overflow: "hidden",
            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
          }}>
            <div style={{
              height: "100%",
              width: `${progress}%`,
              background: "linear-gradient(90deg, #c9a84c, #e8c96a, #c9a84c)",
              backgroundSize: "200% 100%",
              borderRadius: "99px",
              boxShadow: "0 0 12px rgba(201,168,76,0.6)",
              transition: "width 0.08s linear",
              animation: "shimmerBar 1.8s linear infinite",
            }}/>
          </div>

          {/* Percentage + steps */}
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", marginTop: "10px",
          }}>
            <div style={{ display: "flex", gap: "6px" }}>
              {steps.map((_, i) => (
                <div key={i} style={{
                  width: "6px", height: "6px", borderRadius: "50%",
                  background: i <= currentStep ? "#c9a84c" : "rgba(201,168,76,0.2)",
                  transition: "background 0.3s",
                  boxShadow: i === currentStep ? "0 0 6px rgba(201,168,76,0.8)" : "none",
                }}/>
              ))}
            </div>
            <span style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "12px",
              color: "#a08030",
              fontWeight: 600,
              letterSpacing: "0.05em",
            }}>
              {Math.round(progress)}%
            </span>
          </div>
        </div>

        {/* Checkmark when done */}
        {phase === 1 && (
          <div style={{
            marginTop: "16px",
            animation: "checkIn 0.4s cubic-bezier(.16,1,.3,1) both",
            color: "#c9a84c",
            fontSize: "28px",
          }}>✓</div>
        )}
      </div>

      {/* Bottom brand */}
      <div style={{
        position: "absolute", bottom: "28px",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: "10px",
        color: "rgba(160,128,48,0.4)",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
      }}>
        © 2024 Serena Beds Ltd · Staff Portal
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN LOGIN PAGE
═══════════════════════════════════════════════════════ */
const LoginPage = () => {
  const [loading, setLoading]           = useState(false);
  const [showSuccess, setShowSuccess]   = useState(false);
  const [successName, setSuccessName]   = useState("");
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPass, setShowPass]         = useState(false);
  const [emailError, setEmailError]     = useState("");
  const [passError, setPassError]       = useState("");
  const [generalError, setGeneralError] = useState("");
  const [emailFocus, setEmailFocus]     = useState(false);
  const [passFocus, setPassFocus]       = useState(false);

  const navigate = useNavigate();

  const validate = () => {
    let ok = true;
    if (!email) {
      setEmailError("Please enter your work email"); ok = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address"); ok = false;
    } else setEmailError("");

    if (!password) {
      setPassError("Please enter your password"); ok = false;
    } else if (password.length < 6) {
      setPassError("Password must be at least 6 characters"); ok = false;
    } else setPassError("");

    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setEmailError(""); setPassError(""); setGeneralError("");
    localStorage.removeItem("token");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");

    try {
      const response = await axiosInstance.post("/auth/user/login", { email, password });
      if (response.data.success) {
        const user = response.data.user;
        const allowedRoles = ["admin","super admin","sales","deliveries","production manager","production admin"];
        if (user?.role && allowedRoles.includes(user.role.toLowerCase())) {
          localStorage.setItem("isAuthenticated", "true");
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("userRole", user.role.toLowerCase());
          if (user.id)        localStorage.setItem("userId",   user.id);
          if (user.full_name) localStorage.setItem("userName", user.full_name);
          setSuccessName(user.full_name || "");
          setShowSuccess(true);
          const role = user.role.toLowerCase();
          let target = "/";
          if (role === "deliveries") target = "/order-signed-delivery-note";
          else if (role === "sales") target = "/all-orders";
          else if (role === "production admin" || role === "production manager") target = "/production-dashboard";
          setTimeout(() => navigate(target), 3400);
        } else {
          setGeneralError("Access Denied: Your role is not permitted here.");
        }
      } else {
        const msg = response.data.message || "Invalid email or password.";
        setGeneralError(msg); setEmailError(msg); setPassError(" ");
      }
    } catch (error) {
      if (window.__IS_LOGGING_OUT__) return;
      let errMsg = "An error occurred. Please try again.";
      if (error.response) {
        if (error.response.status === 401) errMsg = "Invalid email or password.";
        else if (error.response.data?.message) errMsg = error.response.data.message;
        else errMsg = `Server error: ${error.response.status}`;
      } else if (error.request) {
        errMsg = "Network error. Please check your connection.";
      } else {
        errMsg = error.message || "An unexpected error occurred.";
      }
      setGeneralError(errMsg); setEmailError(errMsg); setPassError(" ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Cormorant+Garamond:ital,wght@1,300;0,400;0,600&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --gold:        #c9a84c;
          --gold-light:  #e8c96a;
          --gold-dark:   #a07828;
          --gold-pale:   #f5ead0;
          --cream:       #fdf9f2;
          --cream-dark:  #f5ede0;
          --warm-brown:  #2c1f0a;
          --mid-brown:   #6b4e1a;
          --text-main:   #3a2c10;
          --text-sub:    #8a6820;
          --text-muted:  #b09040;
          --border:      rgba(201,168,76,0.25);
          --border-focus:rgba(201,168,76,0.85);
          --err:         #c0392b;
          --err-bg:      rgba(192,57,43,0.07);
          --err-border:  rgba(192,57,43,0.3);
          --shadow-card: 0 20px 80px rgba(160,120,40,0.18), 0 4px 24px rgba(160,120,40,0.12);
          --radius:      14px;
        }

        html, body { height: 100%; }

        /* ── Keyframes ─────────────────────────── */
        @keyframes loaderFadeIn     { from{opacity:0} to{opacity:1} }
        @keyframes loaderContentIn  { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes bedFloat         { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes glowPulse        { 0%,100%{opacity:.6;transform:translate(-50%,-50%) scale(1)} 50%{opacity:1;transform:translate(-50%,-50%) scale(1.08)} }
        @keyframes spin             { to{transform:rotate(360deg)} }
        @keyframes shimmerBar       { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes checkIn          { from{opacity:0;transform:scale(0.6)} to{opacity:1;transform:scale(1)} }
        @keyframes pageIn           { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmerBtn       { from{transform:translateX(-100%)} to{transform:translateX(350%)} }
        @keyframes pulseGreen       { 0%,100%{opacity:.4;transform:scale(1)} 50%{opacity:1;transform:scale(1.3)} }
        @keyframes floatLeft        { 0%,100%{transform:translateY(0) rotate(-2deg)} 50%{transform:translateY(-14px) rotate(-2deg)} }
        @keyframes floatRight       { 0%,100%{transform:translateY(0) rotate(2deg)} 50%{transform:translateY(-10px) rotate(2deg)} }
        @keyframes borderGlow       { 0%,100%{box-shadow:0 0 0 3px rgba(201,168,76,0.1)} 50%{box-shadow:0 0 0 5px rgba(201,168,76,0.2)} }

        /* ── Page wrapper ─────────────────────── */
        .sl-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #fdf8ee 0%, #faf3e2 30%, #fdf9f0 60%, #fff8ed 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px 16px;
          position: relative;
          overflow: hidden;
          font-family: 'DM Sans', sans-serif;
          animation: pageIn 0.6s ease both;
        }

        /* ── Left decorative panel ─────────────── */
        .sl-left-panel {
          display: none;
          position: relative;
          flex: 1;
          align-items: center;
          justify-content: center;
          padding: 48px;
          flex-direction: column;
          gap: 32px;
        }

        /* ── Card ───────────────────────────────── */
        .sl-card {
          width: 100%;
          max-width: 460px;
          background: #ffffff;
          border-radius: var(--radius);
          border: 1px solid rgba(201,168,76,0.2);
          padding: clamp(28px, 5vw, 52px) clamp(24px, 5vw, 48px);
          position: relative;
          box-shadow: var(--shadow-card);
          z-index: 10;
        }

        /* ── Input ──────────────────────────────── */
        .sl-input {
          width: 100%;
          border: 1.5px solid var(--border);
          border-radius: 10px;
          padding: 13px 16px 13px 44px;
          background: #fdfaf4;
          color: var(--text-main);
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color 0.22s, box-shadow 0.22s, background 0.22s;
          letter-spacing: 0.01em;
        }
        .sl-input::placeholder { color: rgba(107,78,26,0.35); font-style: italic; }
        .sl-input:focus {
          border-color: var(--gold);
          background: #fff;
          box-shadow: 0 0 0 4px rgba(201,168,76,0.1);
        }
        .sl-input.err {
          border-color: var(--err);
          background: var(--err-bg);
          box-shadow: 0 0 0 3px rgba(192,57,43,0.07);
        }
        .sl-input.pass { padding-right: 48px; }

        /* ── Label ──────────────────────────────── */
        .sl-label {
          display: block;
          font-size: 10.5px; font-weight: 600;
          color: var(--text-sub);
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        /* ── Button ─────────────────────────────── */
        .sl-btn {
          width: 100%; padding: 15px 20px;
          border: none; border-radius: 10px;
          background: linear-gradient(110deg, #a07828 0%, #c9a84c 35%, #e8c96a 55%, #c9a84c 75%, #a07828 100%);
          background-size: 250% 100%;
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-size: 12.5px; font-weight: 600;
          letter-spacing: 0.16em; text-transform: uppercase;
          cursor: pointer;
          position: relative; overflow: hidden;
          transition: background-position 0.5s, box-shadow 0.3s, transform 0.2s;
          box-shadow: 0 4px 20px rgba(201,168,76,0.35);
        }
        .sl-btn:hover:not(:disabled) {
          background-position: right center;
          box-shadow: 0 8px 32px rgba(201,168,76,0.45);
          transform: translateY(-2px);
        }
        .sl-btn:active:not(:disabled) { transform: translateY(0); }
        .sl-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .sl-btn .shine {
          position: absolute; top: 0; bottom: 0; width: 30%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          animation: shimmerBtn 2s ease-in-out infinite;
        }
        .sl-btn-inner {
          position: relative; z-index: 1;
          display: flex; align-items: center; justify-content: center; gap: 9px;
        }

        /* ── Field wrap ─────────────────────────── */
        .sl-field-wrap { position: relative; }
        .sl-field-icon {
          position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
          color: rgba(160,120,48,0.45); pointer-events: none;
          display: flex; align-items: center;
          transition: color 0.22s;
        }
        .sl-field-wrap:focus-within .sl-field-icon { color: var(--gold); }
        .sl-toggle {
          position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
          background: none; border: none; cursor: pointer;
          color: rgba(160,120,48,0.4); display: flex; align-items: center;
          padding: 4px; transition: color 0.22s; border-radius: 4px;
        }
        .sl-toggle:hover { color: var(--gold); }

        /* ── Error text ─────────────────────────── */
        .sl-err-text {
          color: var(--err);
          font-size: 12px; margin-top: 5px; padding-left: 2px;
          display: flex; align-items: center; gap: 5px;
        }

        /* ── Corner brackets ─────────────────────── */
        .sl-corner {
          position: absolute; width: 20px; height: 20px;
        }

        /* ── Ornament row ────────────────────────── */
        .ornament-row {
          display: flex; align-items: center; gap: 12px;
          color: rgba(201,168,76,0.5); font-size: 11px;
        }
        .ornament-row::before, .ornament-row::after {
          content: ''; flex: 1; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent);
        }

        /* ── Scrollbar ───────────────────────────── */
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(201,168,76,0.25); border-radius: 2px; }

        /* ── Responsive ─────────────────────────── */
        @media (min-width: 900px) {
          .sl-page { padding: 32px; }
          .sl-wrapper {
            display: flex; align-items: stretch;
            max-width: 900px; width: 100%;
            background: #fff;
            border-radius: 20px;
            box-shadow: 0 24px 100px rgba(160,120,40,0.18), 0 4px 32px rgba(160,120,40,0.1);
            overflow: hidden;
            border: 1px solid rgba(201,168,76,0.18);
          }
          .sl-left-panel { display: flex; }
          .sl-card {
            max-width: none; flex: 1;
            border-radius: 0; border: none;
            border-left: 1px solid rgba(201,168,76,0.15);
            box-shadow: none;
            padding: 52px 48px;
          }
        }

        @media (max-width: 899px) {
          .sl-wrapper { width: 100%; }
        }

        @media (max-width: 400px) {
          .sl-card { padding: 28px 20px; }
        }
      `}</style>

      <SuccessOverlay visible={showSuccess} userName={successName}/>

      <div className="sl-page">
        {/* BG decorative blobs */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background:
            "radial-gradient(ellipse 60% 60% at 10% 60%, rgba(201,168,76,0.08) 0%, transparent 65%)," +
            "radial-gradient(ellipse 45% 50% at 90% 15%, rgba(201,168,76,0.07) 0%, transparent 60%)," +
            "radial-gradient(ellipse 30% 30% at 80% 85%, rgba(201,168,76,0.05) 0%, transparent 60%)",
        }}/>

        {/* Subtle grid */}
        <div style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(201,168,76,0.05) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(201,168,76,0.05) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}/>

        {/* Floating decorative elements */}
        <div style={{
          position: "absolute", top: "8%", left: "4%",
          opacity: 0.06, pointerEvents: "none",
          animation: "floatLeft 5s ease-in-out infinite",
        }}>
          <BedSVG width={160} height={104} variant="light"/>
        </div>
        <div style={{
          position: "absolute", bottom: "6%", right: "4%",
          opacity: 0.06, pointerEvents: "none",
          animation: "floatRight 6s ease-in-out infinite",
        }}>
          <BedSVG width={120} height={78} variant="light"/>
        </div>

        {/* ── Main wrapper ───────────────────── */}
        <div className="sl-wrapper">

          {/* ── LEFT PANEL (desktop only) ──── */}
          <div className="sl-left-panel" style={{
            background: "linear-gradient(160deg, #fdf6e3 0%, #faf0d0 40%, #f5e8bb 100%)",
            minWidth: "340px",
          }}>
            {/* Decorative circles */}
            <div style={{
              position: "absolute", top: "-60px", left: "-60px",
              width: "220px", height: "220px", borderRadius: "50%",
              background: "radial-gradient(circle, rgba(201,168,76,0.12) 0%, transparent 70%)",
              pointerEvents: "none",
            }}/>
            <div style={{
              position: "absolute", bottom: "-40px", right: "-40px",
              width: "180px", height: "180px", borderRadius: "50%",
              background: "radial-gradient(circle, rgba(201,168,76,0.1) 0%, transparent 70%)",
              pointerEvents: "none",
            }}/>

            <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
              {/* Tagline */}
              <div style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                fontSize: "13px",
                color: "#a08030",
                letterSpacing: "0.12em",
                marginBottom: "32px",
                textTransform: "uppercase",
              }}>
                Crafted for Rest
              </div>

              {/* Bed illustration */}
              <div style={{
                marginBottom: "28px",
                filter: "drop-shadow(0 8px 28px rgba(160,120,48,0.25))",
                animation: "bedFloat 3s ease-in-out infinite",
              }}>
                <BedSVG width={220} height={143} variant="light"/>
              </div>

              {/* Brand name */}
              <div style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "38px", fontWeight: 700,
                color: "#2c1f0a",
                letterSpacing: "0.14em",
                lineHeight: 1,
                marginBottom: "4px",
              }}>
                SERENA
              </div>
              <div style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "10px", fontWeight: 600,
                color: "#c9a84c",
                letterSpacing: "0.45em",
                textTransform: "uppercase",
                marginBottom: "28px",
              }}>
                BEDS LTD
              </div>

              {/* Decorative divider */}
              <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "center", marginBottom: "24px" }}>
                <div style={{ width: "40px", height: "1px", background: "linear-gradient(90deg, transparent, #c9a84c)" }}/>
                <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#c9a84c", opacity: 0.6 }}/>
                <div style={{ width: "40px", height: "1px", background: "linear-gradient(90deg, #c9a84c, transparent)" }}/>
              </div>

              {/* Features */}
              {["Luxury Bed Collections", "Premium Craftsmanship", "Staff Management Portal"].map((text, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "12.5px", color: "#6b4e1a",
                  letterSpacing: "0.03em",
                  marginBottom: "10px",
                  justifyContent: "center",
                }}>
                  <div style={{
                    width: "5px", height: "5px", borderRadius: "50%",
                    background: "#c9a84c", flexShrink: 0,
                  }}/>
                  {text}
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT: LOGIN CARD ────────────── */}
          <div className="sl-card">
            {/* Corner brackets */}
            {[
              { top:"12px",  left:"12px",  borderTop:"1.5px solid rgba(201,168,76,.4)", borderLeft:"1.5px solid rgba(201,168,76,.4)"  },
              { top:"12px",  right:"12px", borderTop:"1.5px solid rgba(201,168,76,.4)", borderRight:"1.5px solid rgba(201,168,76,.4)" },
              { bottom:"12px", left:"12px",  borderBottom:"1.5px solid rgba(201,168,76,.4)", borderLeft:"1.5px solid rgba(201,168,76,.4)"  },
              { bottom:"12px", right:"12px", borderBottom:"1.5px solid rgba(201,168,76,.4)", borderRight:"1.5px solid rgba(201,168,76,.4)" },
            ].map((s, i) => (
              <div key={i} className="sl-corner" style={s}/>
            ))}

            {/* Mobile-only brand header */}
            <div style={{
              textAlign: "center",
              marginBottom: "36px",
            }}>
              {/* Icon */}
              <div style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: "68px", height: "68px",
                background: "linear-gradient(135deg, rgba(201,168,76,0.15), rgba(201,168,76,0.05))",
                border: "1.5px solid rgba(201,168,76,0.3)",
                borderRadius: "14px",
                marginBottom: "18px",
                boxShadow: "0 4px 20px rgba(201,168,76,0.12)",
              }}>
                <BedSVG width={42} height={27} variant="light"/>
              </div>

              <div style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "clamp(22px, 4vw, 28px)", fontWeight: 700,
                color: "#2c1f0a",
                letterSpacing: "0.12em",
                lineHeight: 1,
                marginBottom: "4px",
              }}>
                SERENA
              </div>
              <div style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "9.5px", fontWeight: 600,
                color: "#c9a84c",
                letterSpacing: "0.42em",
                textTransform: "uppercase",
                marginBottom: "20px",
              }}>
                BEDS LTD
              </div>

              <div className="ornament-row">✦</div>

              <div style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic", fontSize: "14px",
                color: "#a08030",
                marginTop: "14px",
                letterSpacing: "0.04em",
              }}>
                Staff Authentication Portal
              </div>
            </div>

            {/* ── FORM ─────────────────────────── */}
            <form onSubmit={handleSubmit} noValidate>

              {/* General error */}
              {generalError && (
                <div style={{
                  background: "var(--err-bg)",
                  border: "1.5px solid var(--err-border)",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  marginBottom: "22px",
                  display: "flex", alignItems: "flex-start", gap: "10px",
                }}>
                  <span style={{ color: "var(--err)", fontSize: "16px", lineHeight: 1.2, flexShrink: 0 }}>⚠</span>
                  <span style={{
                    color: "#a02020",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "13px", lineHeight: 1.5,
                  }}>
                    {generalError}
                  </span>
                </div>
              )}

              {/* Email field */}
              <div style={{ marginBottom: "20px" }}>
                <label className="sl-label">Work Email</label>
                <div className="sl-field-wrap">
                  <span className="sl-field-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </span>
                  <input
                    type="email"
                    className={`sl-input${emailError ? " err" : ""}`}
                    placeholder="your@company.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(""); setGeneralError(""); }}
                    onFocus={() => setEmailFocus(true)}
                    onBlur={() => setEmailFocus(false)}
                    autoComplete="email"
                  />
                </div>
                {emailError && (
                  <div className="sl-err-text">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r=".5" fill="currentColor"/>
                    </svg>
                    {emailError}
                  </div>
                )}
              </div>

              {/* Password field */}
              <div style={{ marginBottom: "28px" }}>
                <label className="sl-label">Password</label>
                <div className="sl-field-wrap">
                  <span className="sl-field-icon">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                  </span>
                  <input
                    type={showPass ? "text" : "password"}
                    className={`sl-input pass${passError && passError.trim() ? " err" : ""}`}
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPassError(""); setGeneralError(""); }}
                    onFocus={() => setPassFocus(true)}
                    onBlur={() => setPassFocus(false)}
                    autoComplete="current-password"
                  />
                  <button type="button" className="sl-toggle" onClick={() => setShowPass(!showPass)} tabIndex={-1}>
                    {showPass ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {passError && passError.trim() && (
                  <div className="sl-err-text">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r=".5" fill="currentColor"/>
                    </svg>
                    {passError}
                  </div>
                )}
              </div>

              {/* Submit button */}
              <button type="submit" className="sl-btn" disabled={loading}>
                <span className="shine"/>
                <span className="sl-btn-inner">
                  {loading ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                        style={{ animation: "spin 0.85s linear infinite" }}>
                        <circle cx="12" cy="12" r="10" strokeOpacity=".2"/>
                        <path d="M12 2a10 10 0 0110 10" strokeLinecap="round"/>
                      </svg>
                      Verifying Credentials…
                    </>
                  ) : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2"/>
                        <path d="M7 11V7a5 5 0 0110 0v4"/>
                      </svg>
                      Access Staff Dashboard
                    </>
                  )}
                </span>
              </button>
            </form>

            {/* Secure badge */}
           <div style={{ marginTop: "28px", textAlign: "center" }}>
  <div style={{
    display: "inline-flex", alignItems: "center", gap: "8px",
    padding: "8px 16px",
    background: "rgba(201,168,76,0.05)",
    borderRadius: "99px",
    border: "1px solid rgba(201,168,76,0.14)",
  }}>
    <div style={{
      width: "6px", height: "6px", borderRadius: "50%",
      background: "#c9a84c",
      boxShadow: "0 0 6px rgba(201,168,76,0.45)",
    }}/>
    <span style={{
      fontFamily: "'DM Sans', sans-serif",
      fontSize: "11px",
      color: "#6b4e1a",
      letterSpacing: "0.06em",
    }}>
      Secure staff portal access
    </span>
  </div>
</div>

            {/* Copyright */}
<div style={{ marginTop: "24px", textAlign: "center" }}>
  <span style={{
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "10px",
    color: "rgba(107,78,26,0.72)",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  }}>
    © 2026 Serena Beds Ltd · All Rights Reserved
  </span>
</div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
